import { motion, useReducedMotion } from "framer-motion";

import { skills } from "../../../content";
import type { SkillCategory } from "../../../types/content";

/**
 * ============================================================================
 * Skills — direct consumer of the content layer.
 * ============================================================================
 *
 * Every string on this page comes from `src/content/data/skills.json` through
 * the loader (Constitution V.4). Three top skills and seven categories, all of
 * it canonical to the master resume. To change what this section says, edit the
 * JSON — there is no copy in this file.
 *
 * THE TEMPLATE ICON LIST IS GONE, and it did not survive as a judgement call.
 * The component that lived here rendered 29 framework logos from
 * `constants/biography.ts` — React, Redux, Kotlin, Android Studio, Figma, Adobe
 * XD — hotlinked to w3.org, wikipedia, github and half a dozen other hosts.
 * Three things ruled them out and any one of them would have been enough:
 *
 *   1. Constitution V.6 names "template skill icons" as Phase 1 dead code. This
 *      is the item, not a reinterpretation of it.
 *   2. They describe someone else. The canonical skill set is 37 delivery,
 *      governance, healthcare-integration and AI-enablement capabilities;
 *      roughly three of them ("MERN Stack", "Next.js", "React Native") have any
 *      icon counterpart at all. An icon rail that illustrates 3 of 37 entries is
 *      decoration that reads as a claim.
 *   3. `skills.json` carries no image field, so an icon could only come from a
 *      component-side map — a hardcoded fact about the author's toolchain,
 *      which is exactly what Article II.2 forbids.
 *
 * Text and tags instead. The skill *is* the content; a logo beside it adds no
 * information and costs eleven third-party hosts a page visit.
 *
 * NO INTRO PARAGRAPH, matching Education and Services: a standfirst about the
 * author's capabilities is content, and content lives in the layer.
 *
 * Every colour is a token from `global/theme/tokens.css`. No token was added.
 */

const SkillCategoryCard = ({ category, index }: { category: SkillCategory; index: number }): JSX.Element => {
	const reduceMotion = useReducedMotion();

	return (
		<motion.li
			className="h-full"
			initial={reduceMotion ? false : { opacity: 0, y: 28 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.2 }}
			transition={{ duration: 0.55, delay: Math.min(index * 0.06, 0.24), ease: [0.22, 1, 0.36, 1] }}
		>
			{/* `card-shell` / `card-face` — the site-wide card (D-061). This section
			    was the flattest surface on the page: an opaque `--surface-card`
			    face, a 1px slate border, no icon, no elevation and no hover beyond
			    a border-colour change. The audit named it the most
			    template-generic section on the site, and it was the only one of
			    the seven that carried no treatment of its own at all. */}
			<div className="card-shell">
				<article className="card-face flex h-full flex-col p-6">
					{/* The brand-ramp hairline the Services and Products cards carry
					    (D-099), so the three card sections on Home read as one
					    system. DECORATIVE, hence the brand ramp rather than a
					    `--cat-*` hue: on this site a category hue is INFORMATION
					    and `skills.json` carries no hue-worthy taxonomy. */}
					<span
						aria-hidden="true"
						className="mb-5 block h-[3px] w-10 rounded-full bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]"
					/>

					<h3 className="text-lg font-bold text-[var(--text-strong)]">{category.name}</h3>

					{/* A real list, not a div of spans: these are the section's payload,
					    and a screen reader should be able to count them. */}
					<ul className="mt-4 flex flex-wrap gap-2">
						{category.skills.map((skill: string) => (
							<li key={skill} className="chip-quiet">
								{skill}
							</li>
						))}
					</ul>
				</article>
			</div>
		</motion.li>
	);
};

/**
 * Renders nothing when the layer carries no categories — Article II.4, as a
 * property of the component rather than of whoever edits the JSON next
 * (the HighlightedStories precedent).
 */
const Skills = (): JSX.Element | null => {
	if (!skills.categories.length) return null;

	return (
		<section className="section-shell" id="skills" aria-labelledby="skills-heading">
			<div className="section-rail">
				{/* Owner ruling 2026-08-19 (D-063): every section carries an eyebrow.
				    Five were added in the noun-phrase register, matching
				    Certifications' existing "Verified Credentials". These are
				    structural labels rather than claims about the author, which is
				    why they live in the component like the two that already did —
				    II.2 forbids hardcoding a career FACT, and a section label is
				    not one. */}
				<p className="section-eyebrow">Practice Areas</p>

				<h2 id="skills-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Skills
					</span>
				</h2>

				{/* `topSkills` is the lead block — a distinct claim from the
				    categories below, so it gets a distinct register rather than
				    being folded in as an eighth card. Guarded separately: the
				    categories can carry the section on their own.

				    IT LEADS WITH ENGINEERING AND WITH SCRUM AS OF D-100, on the
				    owner's instruction, and the two added entries are NOT new
				    claims: "Agile (Scrum)" and "MERN Stack" are copied verbatim
				    out of the categories below, so this changed which of the
				    owner's recorded skills lead the section and asserted nothing
				    the layer did not already hold. Both are independently
				    evidenced by credentials this site publishes — CSM and CSPO
				    from Scrum Alliance, and the CloudNet MERN Stack Development
				    certificate. The block was sourced from LinkedIn's "Top
				    Skills" panel and no longer matches it; that is a deliberate
				    editorial divergence rather than drift.

				    The pills are the shared tinted label now (D-061). They were the
				    site's fifth tint family — 14px, `--accent-marker` edge over
				    `--accent-marker-fill` — which is the same indigo idea as the
				    Education year pill in a different size and a different pair of
				    tokens. One idea, one implementation. */}
				{skills.topSkills.length > 0 && (
					<ul aria-label="Top skills" className="mt-8 flex flex-wrap justify-center gap-3">
						{skills.topSkills.map((skill: string) => (
							<li
								key={skill}
								className="pill-tint border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] text-[var(--cat-indigo-text)]"
							>
								{skill}
							</li>
						))}
					</ul>
				)}

				{/* `auto-rows-fr` AND `grid-balanced`, for the two separate defects
				    D-099 found in the Services grid and this one shares.

				    A CSS grid row stretches its items to the tallest of THAT row,
				    so the cards within a row already matched and nothing matched
				    them across rows: seven categories of six or seven chips each
				    produced a step from row to row that reads as sloppiness rather
				    than as a design. `auto-rows-fr` makes every row track equal.

				    And seven categories in three columns leaves ONE card alone on
				    the last row, flush left with two empty columns beside it —
				    which is the same arithmetic the seven services have.
				    `.grid-balanced` centres it, with a selector that says "the last
				    card is the first of its row" rather than naming the number
				    seven, so it survives the eighth category (D-089). */}
				<ul className="grid-balanced mt-12 grid w-full auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 xl:grid-cols-3 xl:gap-8">
					{skills.categories.map((category: SkillCategory, index: number) => (
						<SkillCategoryCard key={category.id} category={category} index={index} />
					))}
				</ul>
			</div>
		</section>
	);
};

export default Skills;
